import { useMemo, useState } from "react";

import { ApiError, apiRequest } from "@/lib/api-client";
import type { JsonValue, WorkbenchAction } from "@/types";

import { JsonView } from "./json-view";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

interface WorkbenchActionCardProps {
  action: WorkbenchAction;
}

function parseBody(input: string): Record<string, JsonValue> | undefined {
  const normalized = input.trim();
  if (normalized.length === 0) {
    return undefined;
  }

  const parsed = JSON.parse(normalized) as unknown;
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
    return parsed as Record<string, JsonValue>;
  }
  throw new Error("Request body must be a JSON object.");
}

export function WorkbenchActionCard({ action }: WorkbenchActionCardProps) {
  const defaultBodyText = useMemo(
    () => (action.defaultBody ? JSON.stringify(action.defaultBody, null, 2) : ""),
    [action.defaultBody],
  );

  const [endpoint, setEndpoint] = useState(action.endpoint);
  const [bodyText, setBodyText] = useState(defaultBodyText);
  const [response, setResponse] = useState<unknown>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onRun(): Promise<void> {
    setRunning(true);
    setError(null);

    try {
      const body =
        action.method === "GET" || action.method === "DELETE" ? undefined : parseBody(bodyText);
      const result = await apiRequest<unknown>(endpoint, action.method, body);
      setResponse(result);
    } catch (rawError) {
      if (rawError instanceof ApiError) {
        setError(rawError.message);
      } else if (rawError instanceof Error) {
        setError(rawError.message);
      } else {
        setError("Unexpected failure while running request.");
      }
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="rounded-xl border border-border bg-panel p-3 md:p-4">
      <div className="mb-2 md:mb-3 flex items-center justify-between gap-2 md:gap-4">
        <div className="min-w-0">
          <h3 className="text-sm md:text-base font-semibold text-text truncate">{action.label}</h3>
          <p className="text-xs text-muted truncate">{action.description ?? "Ad-hoc API action"}</p>
        </div>
        <span className="rounded-full border border-brand-500/40 bg-brand-500/10 px-2 py-1 text-xs text-brand-300">
          {action.method}
        </span>
      </div>

      <Label htmlFor={`${action.id}-endpoint`}>Endpoint</Label>
      <Input
        id={`${action.id}-endpoint`}
        value={endpoint}
        onChange={(event) => setEndpoint(event.target.value)}
        className="mb-3"
      />

      {action.method !== "GET" && action.method !== "DELETE" ? (
        <>
          <Label htmlFor={`${action.id}-body`}>JSON body</Label>
          <Textarea
            id={`${action.id}-body`}
            className="mb-3"
            value={bodyText}
            onChange={(event) => setBodyText(event.target.value)}
            rows={8}
          />
        </>
      ) : null}

      <Button className="mb-3" onClick={onRun} disabled={running}>
        {running ? "Running..." : "Run request"}
      </Button>

      {error ? <p className="mb-3 rounded-md bg-red-950/40 p-2 text-xs text-red-200">{error}</p> : null}

      <JsonView value={response} />
    </div>
  );
}
